import {
  Box,
  Button,
  Checkbox,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Select,
  Text,
  VStack
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreateMessageApi, UpdateMessageApi } from '../../../apis/Message.api';
import { GetMessageApi } from '../../../apis/Message.api';
import {
  initStateData,
  MessageStateType,
  SetMessageData
} from '../../../redux/message/MessageSlice';
import { RootState } from '../../../redux/RootReducer';
import { BanInputType, CustomStringInput } from '../../atoms/CustomStringInput';
import { CategorySendMessageType } from '../../constants/MaintenanceConstants';
import MessageListBox from './MessageListBox';
import Encoding from 'encoding-japanese';

const LevelEnum = {
  None: 0,
  WellKnown: 1,
  Caution: 2,
  Warning: 3,
  Emergency: 4
};

export const DivisionEnum = {
  Tram: 1,
  Train: 2,
  Location: 3
};

export interface MessageType {
  categories: Array<MeaageCategory>;
  trains: Array<MessageTrain>;
  train_groups: Array<MessageTrainGroup>;
  signages: Array<MessageSignage>;
}

export interface MessageTrain {
  train_no: number;
  train_group_id: number;
  course: number | null;
  course_name: string | null;
  staff_no: number | null;
}
interface MeaageCategory {
  id: number;
  name: string;
  type: string;
  messages: Array<message>;
}

interface message {
  id: number;
  message: string;
}

export interface MessageSignage {
  name: string;
  signage_id: number;
  inbound: number;
  courses: Array<course>;
}

interface course {
  course: number;
}
interface MessageTrainGroup {
  id: number;
  name: string;
}

interface SignageInfo {
  popup_flg: boolean;
  Arraysignage_id: Array<number | undefined> | null;
}

interface TrainInfo {
  train_no: number | null;
  staff_no: number | null;
}

const Cp932Array = [33120, 33121, 33148, 33169, 33170, 33226, 33116];

const CharacterConvertToShiftJisCode = (character: string) => {
  // messageから一文字ずつunicodeへ変換
  const unicode = Encoding.stringToCode(character);
  //unicodeをutf-8へ変換
  const utf = Encoding.convert(unicode, {
    to: 'UTF8',
    from: 'UNICODE'
  });
  // utf-8をsjisへ変換
  const sjis = Encoding.convert(utf, {
    to: 'SJIS',
    from: 'UTF8'
  });

  // 上で取得したsjisの配列から10進数の文字コードを取得
  const charCode = GetShiftJisCharacterCode(sjis);
  return charCode;
};

const PhohibitionCharacterCheck = (message: string) => {
  const phohibitionCharaArray = [];

  for (let i = 0; i < message.length; i++) {
    const charCode = CharacterConvertToShiftJisCode(message[i]);

    // NEC特殊文字があるか
    if (34624 <= charCode && charCode <= 34719) {
      phohibitionCharaArray.push(message[i]);
    }
    // IBM拡張文字があるか
    else if (64064 <= charCode && charCode <= 64591) {
      phohibitionCharaArray.push(message[i]);
    }
    // NEC選定IBM拡張文字があるか
    else if (60736 <= charCode && charCode <= 61183) {
      phohibitionCharaArray.push(message[i]);
    }
    //cp932特殊文字？があるか（～、||など）
    else if (Cp932Array.includes(charCode)) {
      phohibitionCharaArray.push(message[i]);
    }
  }
  return phohibitionCharaArray;
};

// Encoding.convertで取得したsjisの配列から10進数の文字コードを取得
const GetShiftJisCharacterCode = (sjis: number[]) => {
  const prefix = '0x';
  let charaCode = '';
  charaCode = charaCode.concat(prefix);
  for (let i = 0; i < sjis.length; i++) {
    charaCode = charaCode.concat(sjis[i].toString(16));
  }

  // forで作成した16進数の文字コードをNumberでキャストし、10進数に変え返す
  return Number(charaCode);
};

const CategoryInitCheck = (
  messageInfo: MessageType | undefined,
  category: number
) => {
  if (
    messageInfo?.categories
      .find((elem) => elem.id === category)
      ?.name.includes('初期化') ||
    messageInfo?.categories.find((elem) => elem.id === category)?.name ===
      undefined
  ) {
    return true;
  }
  return false;
};

const GetFromMinDate = () => {
  const date = new Date();
  return (
    date.getFullYear().toString() +
    '-' +
    ((date.getMonth() + 1).toString().length === 1 ? '0' : '') +
    (date.getMonth() + 1).toString() +
    '-' +
    (date.getDate().toString().length === 1 ? '0' : '') +
    date.getDate().toString() +
    'T' +
    (date.getHours().toString().length === 1 ? '0' : '') +
    date.getHours().toString() +
    ':' +
    (date.getMinutes().toString().length === 1 ? '0' : '') +
    date.getMinutes().toString()
  );
};

const TrainNumberExtract = (query: string) => {
  if (query !== '') {
    const trainNo = query.substring(query.indexOf('=') + 1);
    return trainNo;
  }
  return '';
};

const GetDivisionCategoryType = (division: {
  tram: boolean;
  train: boolean;
  location: boolean;
}) => {
  if (division.tram && !division.train && !division.location) {
    return CategorySendMessageType.Signage;
  } else if (!division.tram && division.train && !division.location) {
    return CategorySendMessageType.Obc;
  } else if (!division.tram && !division.train && division.location) {
    return CategorySendMessageType.Location;
  } else if (!division.tram && !division.train && !division.location) {
    return CategorySendMessageType.None;
  } else {
    return CategorySendMessageType.Message;
  }
};

// メッセージ一覧画面から表示されているかチェック
const MessageStateExists = (state: MessageStateType) => {
  // Reduxから取得したMessageデータを初期値と比較
  if (JSON.stringify(state) === JSON.stringify(initStateData)) {
    return false;
  }
  return true;
};

// メッセージ一覧画面から表示された際に、どのデータが入力されているかチェックを行う
const MessageInfoExists = (
  stateExists: boolean,
  messageData: MessageStateType,
  type: number
) => {
  // メッセージ一覧画面から表示されているか
  if (stateExists) {
    switch (type) {
      case DivisionEnum.Tram:
        if (messageData.messageSignagesData !== null) {
          return true;
        }
        break;
      case DivisionEnum.Train:
        if (messageData.messageTrainsData !== null) {
          return true;
        }
        break;
      case DivisionEnum.Location:
        if (messageData.messageLocationData !== null) {
          return true;
        }
        break;
    }
  }
  return false;
};

const GetStateCategory = (
  stateExists: boolean,
  messageStateData: MessageStateType
) => {
  if (stateExists) {
    return messageStateData.category_id;
  }
  return 0;
};

const GetStateTrain = (
  stateExists: boolean,
  messageStateData: MessageStateType
) => {
  if (stateExists) {
    const trains = messageStateData.messageTrainsData?.map((data) => {
      return data.train_no;
    });
    if (trains !== undefined) {
      return trains;
    }
  }
  return [];
};

const GetStateTram = (
  stateExists: boolean,
  messageStateData: MessageStateType
) => {
  if (stateExists) {
    const trams = messageStateData.messageSignagesData?.signageDatas.map(
      (data) => {
        return data.signage_id;
      }
    );
    if (trams !== undefined) {
      return trams;
    }
  }
  return [];
};

const GetStateTime = (term: string) => {
  const date = new Date(term);

  return (
    date.getFullYear().toString() +
    '-' +
    ((date.getMonth() + 1).toString().length === 1 ? '0' : '') +
    (date.getMonth() + 1).toString() +
    '-' +
    (date.getDate().toString().length === 1 ? '0' : '') +
    date.getDate().toString() +
    'T' +
    (date.getHours().toString().length === 1 ? '0' : '') +
    date.getHours().toString() +
    ':' +
    (date.getMinutes().toString().length === 1 ? '0' : '') +
    date.getMinutes().toString()
  );
};

// 個別通知クリック後に、表示する分割ボタンを取得
const GetSeparateButtonValue = (divisionCheck: {
  tram: boolean;
  train: boolean;
  location: boolean;
}) => {
  if (divisionCheck.tram) {
    return DivisionEnum.Tram;
  } else if (divisionCheck.train) {
    return DivisionEnum.Train;
  } else if (divisionCheck.location) {
    return DivisionEnum.Location;
  }
  return 0;
};

// 車両やメッセージ一覧画面から表示された場合の分割ボタンの初期表示判定処理
const GetInitSeparateButtonValue = (
  stateExists: boolean,
  queryTrainNo: string,
  messageData: MessageStateType
) => {
  if (queryTrainNo !== '') {
    return DivisionEnum.Train;
  }

  if (stateExists) {
    if (messageData.messageSignagesData !== null) {
      return DivisionEnum.Tram;
    } else if (messageData.messageTrainsData !== null) {
      return DivisionEnum.Train;
    } else if (messageData.messageLocationData !== null) {
      return DivisionEnum.Location;
    }
  }
  return 0;
};

const GetMessageInputCheckData = (addData: string | undefined) => {
  if (addData === undefined) {
    return { Length: 300, BanInputType: BanInputType.None, Value: '' };
  }
  return { Length: 300, BanInputType: BanInputType.None, Value: addData };
};

export const MessageBody: React.FC = () => {
  const navigate = useNavigate();
  const query = useLocation();
  const queryTrainNo = TrainNumberExtract(query.search);
  const messageStateData = useSelector((state: RootState) => state.message);
  const dispatch = useDispatch();
  const stateExists = MessageStateExists(messageStateData.value.data);

  const [noticeRadioValue, setNoticeRadioValue] = useState(
    queryTrainNo !== '' || stateExists ? '2' : '1'
  );
  const [separateButtonValue, setSeparateButtonValue] = useState(
    GetInitSeparateButtonValue(
      stateExists,
      queryTrainNo,
      messageStateData.value.data
    )
  );

  const [divisionCheck, setDivisionCheck] = useState({
    tram: MessageInfoExists(
      stateExists,
      messageStateData.value.data,
      DivisionEnum.Tram
    ),
    train:
      (queryTrainNo !== '' ? true : false) ||
      MessageInfoExists(
        stateExists,
        messageStateData.value.data,
        DivisionEnum.Train
      ),
    location: MessageInfoExists(
      stateExists,
      messageStateData.value.data,
      DivisionEnum.Location
    )
  });
  const [popupCheck, setPopupCheck] = useState({
    tram: MessageInfoExists(
      stateExists,
      messageStateData.value.data,
      DivisionEnum.Tram
    )
      ? messageStateData.value.data.messageSignagesData?.popup_flg
      : false,
    location: MessageInfoExists(
      stateExists,
      messageStateData.value.data,
      DivisionEnum.Location
    )
      ? messageStateData.value.data.messageLocationData?.popup_flg
      : false
  });

  const [alertLevel, setAlertLevel] = useState(
    stateExists ? messageStateData.value.data.alert_level : LevelEnum.None
  );
  const [messageInfo, setMessageInfo] = useState<MessageType>();
  const [categoryChoose, setCategoryChoose] = useState(
    GetStateCategory(stateExists, messageStateData.value.data)
  );
  const [messageChoose, setMessageChoose] = useState<string | undefined>(
    stateExists ? messageStateData.value.data.message : ''
  );
  const [termFrom, setTermFrom] = useState(
    stateExists
      ? GetStateTime(messageStateData.value.data.term_from)
      : GetFromMinDate()
  );
  const [termTo, setTermTo] = useState(
    stateExists ? GetStateTime(messageStateData.value.data.term_to) : ''
  );

  const [tramList, setTramList] = useState<Array<number>>(
    GetStateTram(stateExists, messageStateData.value.data)
  );
  const [trainList, setTrainList] = useState<Array<number>>(
    queryTrainNo !== ''
      ? [Number(queryTrainNo)]
      : GetStateTrain(stateExists, messageStateData.value.data)
  );

  const [englishMessage, setEnglishMessage] = useState(
    stateExists
      ? messageStateData.value.data.messageLocationData?.message_en
      : ''
  );
  const [link, setLink] = useState(
    stateExists ? messageStateData.value.data.messageLocationData?.link_url : ''
  );

  const searchTrainRefA = useRef<HTMLSelectElement | null>(null);
  const searchTrainRefB = useRef<HTMLSelectElement | null>(null);
  const searchTramRefA = useRef<HTMLSelectElement | null>(null);
  const searchTramRefB = useRef<HTMLSelectElement | null>(null);

  const AllChooseButtonClick = useCallback(
    (
      searchRefA: React.MutableRefObject<HTMLSelectElement | null>,
      Division: number
    ) => {
      if (
        searchRefA == null ||
        searchRefA.current == null ||
        searchRefA.current.options.length === 0
      )
        return;

      const listArray = Array.from(searchRefA.current.options).map((data) =>
        Number(data.value)
      );
      if (Division === DivisionEnum.Train) {
        setTrainList(trainList.concat(listArray));
      } else {
        setTramList(tramList.concat(listArray));
      }
    },
    [trainList, tramList]
  );

  const AllReleaseButtonClick = useCallback((Division: number) => {
    if (Division === DivisionEnum.Train) {
      setTrainList([]);
    } else {
      setTramList([]);
    }
  }, []);

  const RightArrowButtonClick = useCallback(
    (
      searchRefA: React.MutableRefObject<HTMLSelectElement | null>,
      Division: number
    ) => {
      if (searchRefA == null || searchRefA.current == null) return;
      const listArray = Array.from(searchRefA.current.options)
        .filter((elem) => elem.selected)
        .map((data) => Number(data.value));

      if (Division === DivisionEnum.Train) {
        setTrainList(trainList.concat(listArray));
      } else {
        setTramList(tramList.concat(listArray));
      }
    },
    [trainList, tramList]
  );

  const LeftArrowButtonClick = useCallback(
    (
      searchRefB: React.MutableRefObject<HTMLSelectElement | null>,
      Division: number
    ) => {
      if (searchRefB == null || searchRefB.current == null) return;
      const listArray = Array.from(searchRefB.current.options)
        .filter((elem) => elem.selected)
        .map((data) => Number(data.value));

      if (Division === DivisionEnum.Train) {
        setTrainList(
          trainList.filter((data) => !listArray.find((elem) => elem === data))
        );
      } else {
        setTramList(
          tramList.filter((data) => !listArray.find((elem) => elem === data))
        );
      }
    },
    [trainList, tramList]
  );

  const GetApi = async () => {
    const response = await GetMessageApi();
    if (response.success && response.data !== null) {
      setMessageInfo(response.data);
    }
  };

  const PostApi = async () => {
    let trains: Array<TrainInfo | undefined> = [];
    const signages: SignageInfo | undefined = {
      popup_flg: false,
      Arraysignage_id: []
    };

    if (noticeRadioValue === '2') {
      if (divisionCheck.train) {
        trains = trainList.map((data) => {
          const findData = messageInfo?.trains.find(
            (elem) => elem.train_no === data
          );
          if (findData !== undefined) {
            return { train_no: findData.train_no, staff_no: findData.staff_no };
          }
        });
      }
      if (divisionCheck.tram) {
        signages.Arraysignage_id = tramList.map((data) => {
          const findData = messageInfo?.signages.find(
            (elem) => elem.signage_id === data
          );
          if (findData !== undefined) {
            return findData.signage_id;
          }
        });
      }
    }
    signages.popup_flg =
      popupCheck.tram === undefined ? false : popupCheck.tram;

    const postData = {
      signage_check: divisionCheck.tram,
      train_check: divisionCheck.train,
      location_check: divisionCheck.location,
      alert_level: alertLevel,
      category_id: categoryChoose,
      message: CategoryInitCheck(messageInfo, categoryChoose)
        ? ''
        : messageChoose,
      term_from: termFrom
        .replace('-', '')
        .replace('-', '')
        .replace('T', '')
        .replace(':', ''),
      term_to: termTo
        .replace('-', '')
        .replace('-', '')
        .replace('T', '')
        .replace(':', ''),
      notice_to: Number(noticeRadioValue),
      signageInfo: signages,
      trainInfo: trains,
      location_info: {
        popup_flg: popupCheck.location,
        link_url: link !== undefined ? link : '',
        message_en: englishMessage !== undefined ? englishMessage : ''
      }
    };

    dispatch(SetMessageData({ data: initStateData }));

    if (stateExists) {
      return await UpdateMessageApi(messageStateData.value.data.id, postData);
    } else {
      return await CreateMessageApi(postData);
    }
  };
  const InputCheck = () => {
    const messageArray = [];
    if (
      (divisionCheck.tram || divisionCheck.location) &&
      alertLevel === LevelEnum.None
    ) {
      messageArray.push('警戒レベルを選択して下さい。');
    }
    if (categoryChoose === 0) {
      messageArray.push('カテゴリを選択して下さい。');
    }
    if (
      !CategoryInitCheck(messageInfo, categoryChoose) &&
      messageChoose === ''
    ) {
      messageArray.push('メッセージを入力して下さい。');
    }
    if (termFrom === '') {
      messageArray.push('期間（自）を選択して下さい。');
    }
    if (termTo === '') {
      messageArray.push('期間（至）を選択して下さい。');
    }
    if (termFrom > termTo) {
      messageArray.push('期間指定を正しく選択して下さい。');
    }
    if (noticeRadioValue === '2') {
      if (divisionCheck.tram) {
        if (tramList.length === 0) {
          messageArray.push('通知先停留場を選択して下さい。');
        }
      }
      if (divisionCheck.train) {
        if (trainList.length === 0) {
          messageArray.push('通知先車両を選択して下さい。');
        }
      }
    }
    return messageArray;
  };

  const SendClick = async () => {
    // 入力がされていない箇所がないかチェックを行う。
    const checks = InputCheck();
    // InputCheck関数で引っかかったエラーを改行で分け、アラートに表示する。
    if (checks.length !== 0) {
      let text = '';
      for (let i = 0; i < checks.length; i++) {
        text += i === 0 ? checks[i] : '\n' + checks[i];
      }
      alert(text);
      return;
    }

    // 禁止文字が存在するか確認
    if (messageChoose !== undefined) {
      const PhohibitionArray = PhohibitionCharacterCheck(messageChoose);
      if (0 < PhohibitionArray.length) {
        let alertMessage =
          'メッセージに禁止文字が含まれています。\n現在入力されている禁止文字：\n';
        for (let i = 0; i < PhohibitionArray.length; i++) {
          alertMessage = alertMessage.concat(PhohibitionArray[i] + '\n');
        }
        alert(alertMessage);
        return;
      }
    }

    const response = await PostApi();
    if (response.success) {
      if (stateExists) {
        navigate('/messageList');
      } else {
        navigate('/location');
      }
      return;
    }
    alert('登録に失敗しました。');
    return;
  };

  useEffect(() => {
    GetApi();
  }, []);

  // 通知ラジオボタンが切り替わった際
  useEffect(() => {
    if (noticeRadioValue === '1' && divisionCheck.location) {
      setSeparateButtonValue(DivisionEnum.Location);
    }
  }, [divisionCheck.location, noticeRadioValue, separateButtonValue]);

  return (
    <>
      <Box
        fontSize={18}
        position='relative'
        display='grid'
        gridTemplateColumns='1.2fr 1.5fr'
      >
        <HStack gridColumn='1/2'>
          <Text mt={5} w='80%' align='right'>
            区分：
          </Text>
        </HStack>
        <HStack mt={3} gridColumn='2/2'>
          <Checkbox
            onChange={(e) => {
              setDivisionCheck({ ...divisionCheck, tram: e.target.checked });
              setCategoryChoose(0);
              setMessageChoose('');
              setPopupCheck({ ...popupCheck, tram: false });
              setSeparateButtonValue(
                GetSeparateButtonValue({
                  ...divisionCheck,
                  tram: e.target.checked
                })
              );
            }}
            isChecked={divisionCheck.tram ? true : false}
          >
            電停
          </Checkbox>
          <Checkbox
            onChange={(e) => {
              setDivisionCheck({ ...divisionCheck, train: e.target.checked });
              setCategoryChoose(0);
              setMessageChoose('');
              setSeparateButtonValue(
                GetSeparateButtonValue({
                  ...divisionCheck,
                  train: e.target.checked
                })
              );
            }}
            isChecked={divisionCheck.train ? true : false}
          >
            車両
          </Checkbox>
          <Checkbox
            onChange={(e) => {
              setDivisionCheck({
                ...divisionCheck,
                location: e.target.checked
              });
              setCategoryChoose(0);
              setMessageChoose('');
              setPopupCheck({ ...popupCheck, location: false });
              setSeparateButtonValue(
                GetSeparateButtonValue({
                  ...divisionCheck,
                  location: e.target.checked
                })
              );
            }}
            isChecked={divisionCheck.location ? true : false}
          >
            位置情報
          </Checkbox>
        </HStack>
        {(divisionCheck.tram || divisionCheck.location) && (
          <>
            <HStack mt={3} gridColumn='1/2'>
              <Text w='80%' align='right'>
                警戒レベル：
              </Text>
            </HStack>
            <HStack mt={3} gridColumn='2/2'>
              <Button
                border='4px'
                borderColor='#D14042'
                bgColor={alertLevel === LevelEnum.Emergency ? '#D14042' : ''}
                _focus={{ bgColor: '#D14042' }}
                _hover={{ bgColor: '#D14042' }}
                onClick={() => {
                  setAlertLevel(LevelEnum.Emergency);
                }}
              >
                緊急
              </Button>
              <Button
                border='4px'
                borderColor='#FFA520'
                bgColor={alertLevel === LevelEnum.Warning ? '#FFA520' : ''}
                _focus={{ bgColor: '#FFA520' }}
                _hover={{ bgColor: '#FFA520' }}
                onClick={() => {
                  setAlertLevel(LevelEnum.Warning);
                  popupCheck.tram &&
                    setPopupCheck({ ...popupCheck, tram: false });
                  popupCheck.location &&
                    setPopupCheck({ ...popupCheck, location: false });
                }}
              >
                警告
              </Button>
              <Button
                border='4px'
                borderColor='#F4D54F'
                bgColor={alertLevel === LevelEnum.Caution ? '#F4D54F' : ''}
                _focus={{ bgColor: '#F4D54F' }}
                _hover={{ bgColor: '#F4D54F' }}
                onClick={() => {
                  setAlertLevel(LevelEnum.Caution);
                  popupCheck.tram &&
                    setPopupCheck({ ...popupCheck, tram: false });
                  popupCheck.location &&
                    setPopupCheck({ ...popupCheck, location: false });
                }}
              >
                注意
              </Button>
              <Button
                border='4px'
                borderColor='#69A440'
                bgColor={alertLevel === LevelEnum.WellKnown ? '#69A440' : ''}
                _focus={{ bgColor: '#69A440' }}
                _hover={{ bgColor: '#69A440' }}
                onClick={() => {
                  setAlertLevel(LevelEnum.WellKnown);
                  popupCheck.tram &&
                    setPopupCheck({ ...popupCheck, tram: false });
                  popupCheck.location &&
                    setPopupCheck({ ...popupCheck, location: false });
                }}
              >
                周知
              </Button>
            </HStack>
          </>
        )}
        <HStack mt={3} gridColumn='1/2'>
          <Text w='80%' align='right'>
            カテゴリ：
          </Text>
        </HStack>

        <HStack mt={3} gridColumn='2/2'>
          <Select
            w='80%'
            value={categoryChoose}
            onChange={(e) => {
              setCategoryChoose(Number(e.target.value));
            }}
          >
            <option value={0}>{'カテゴリを選択して下さい。'}</option>
            {messageInfo?.categories.map((data, i) => {
              if (data.type === GetDivisionCategoryType(divisionCheck)) {
                return (
                  <option key={'category_' + i} value={data.id}>
                    {data.name}
                  </option>
                );
              }
            })}
          </Select>
        </HStack>
        {!CategoryInitCheck(messageInfo, categoryChoose) && (
          <>
            <HStack gridColumn='1/2'>
              <Text w='80%' align='right'>
                定型文：
              </Text>
            </HStack>
            <HStack mt={3} gridColumn='2/2'>
              <select
                style={{
                  width: '50%',
                  border: '1px solid',
                  overflowX: 'auto'
                }}
                size={4}
                onChange={(e) => {
                  setMessageChoose(e.target[e.target.selectedIndex].innerText);
                }}
              >
                {messageInfo?.categories.map((data, i) => {
                  if (data.id === categoryChoose) {
                    return messageInfo.categories[i].messages.map(
                      (message, j) => {
                        return (
                          <option
                            key={'message_' + i + '_' + j}
                            value={message.id}
                          >
                            {message.message}
                          </option>
                        );
                      }
                    );
                  }
                })}
              </select>
            </HStack>
          </>
        )}
        {!CategoryInitCheck(messageInfo, categoryChoose) && (
          <>
            <HStack mt={3} gridColumn='1/2'>
              <Text w='80%' align='right'>
                メッセージ：
              </Text>
            </HStack>
            <HStack mt={3} gridColumn='2/2'>
              <CustomStringInput
                CheckInfo={GetMessageInputCheckData('')}
                inputValue={messageChoose}
                setValue={setMessageChoose}
                PhohibitionCharaCheck={true}
              />
            </HStack>
          </>
        )}
        {(divisionCheck.tram || divisionCheck.location) &&
          alertLevel === LevelEnum.Emergency && (
            <>
              <HStack mt={3} gridColumn='1/2'>
                <Text w='80%' align='right'>
                  ポップアップ表示：
                </Text>
              </HStack>
              <HStack mt={3} gridColumn='2/2'>
                {divisionCheck.tram && (
                  <Checkbox
                    onChange={(e) => {
                      setPopupCheck({ ...popupCheck, tram: e.target.checked });
                    }}
                    isChecked={popupCheck.tram ? true : false}
                  >
                    電停
                  </Checkbox>
                )}
                {divisionCheck.location && (
                  <Checkbox
                    onChange={(e) => {
                      setPopupCheck({
                        ...popupCheck,
                        location: e.target.checked
                      });
                    }}
                    isChecked={popupCheck.location ? true : false}
                  >
                    位置情報
                  </Checkbox>
                )}
              </HStack>
            </>
          )}
        <HStack mt={3} gridColumn='1/2'>
          <Text w='80%' align='right'>
            期間：
          </Text>
        </HStack>
        <HStack mt={3} gridColumn='2/2'>
          <Input
            type='datetime-local'
            w='20%'
            onChange={(e) => {
              setTermFrom(e.target.value);
            }}
            value={termFrom}
            min={GetFromMinDate()}
          />
          <Text>～</Text>
          <Input
            type='datetime-local'
            w='20%'
            value={termTo}
            bgColor={termTo !== '' && termFrom > termTo ? 'red' : ''}
            onChange={(e) => {
              setTermTo(e.target.value);
            }}
            min={termFrom}
          />
        </HStack>

        {(divisionCheck.tram || divisionCheck.train) && (
          <>
            <HStack mt={3} gridColumn='1/2'>
              <Text w='80%' align='right'>
                通知：
              </Text>
            </HStack>

            <HStack mt={3} gridColumn='2/2'>
              <RadioGroup
                onChange={setNoticeRadioValue}
                value={noticeRadioValue}
              >
                <Radio value='1'>一斉通知</Radio>
                <Radio ml={5} value='2'>
                  個別通知
                </Radio>
              </RadioGroup>
            </HStack>
          </>
        )}
        <HStack mt={3} gridColumn='2/2'>
          {noticeRadioValue === '2' && (
            <>
              {divisionCheck.tram && (
                <Button
                  _hover={{ bgColor: '' }}
                  _focus={{ bgColor: '' }}
                  bgColor={
                    separateButtonValue === DivisionEnum.Tram
                      ? 'blackAlpha.500'
                      : ''
                  }
                  onClick={() => setSeparateButtonValue(DivisionEnum.Tram)}
                >
                  電停
                </Button>
              )}
              {divisionCheck.train && (
                <Button
                  _hover={{ bgColor: '' }}
                  _focus={{ bgColor: '' }}
                  bgColor={
                    separateButtonValue === DivisionEnum.Train
                      ? 'blackAlpha.500'
                      : ''
                  }
                  onClick={() => setSeparateButtonValue(DivisionEnum.Train)}
                >
                  車両
                </Button>
              )}
            </>
          )}
          {divisionCheck.location && (
            <Button
              _hover={{ bgColor: '' }}
              _focus={{ bgColor: '' }}
              bgColor={
                separateButtonValue === DivisionEnum.Location
                  ? 'blackAlpha.500'
                  : ''
              }
              onClick={() => setSeparateButtonValue(DivisionEnum.Location)}
            >
              位置情報
            </Button>
          )}
        </HStack>
        {separateButtonValue === DivisionEnum.Tram &&
          noticeRadioValue === '2' &&
          divisionCheck.tram && (
            <MessageListBox
              MessageInfo={messageInfo}
              Division={DivisionEnum.Tram}
              ChooseTitle='上下選択'
              NoticeTitle='通知先停留場'
              ChooseList={tramList}
              searchRefA={searchTrainRefA}
              searchRefB={searchTrainRefB}
              RightButtonOnClick={RightArrowButtonClick}
              LeftButtonOnClick={LeftArrowButtonClick}
              AllChooseButtonClick={AllChooseButtonClick}
              AllReleaseButtonClick={AllReleaseButtonClick}
            />
          )}
        {separateButtonValue === DivisionEnum.Train &&
          noticeRadioValue === '2' &&
          divisionCheck.train && (
            <MessageListBox
              MessageInfo={messageInfo}
              Division={DivisionEnum.Train}
              ChooseTitle='形式選択'
              NoticeTitle='通知先車両'
              ChooseList={trainList}
              searchRefA={searchTramRefA}
              searchRefB={searchTramRefB}
              RightButtonOnClick={RightArrowButtonClick}
              LeftButtonOnClick={LeftArrowButtonClick}
              AllChooseButtonClick={AllChooseButtonClick}
              AllReleaseButtonClick={AllReleaseButtonClick}
            />
          )}
        {separateButtonValue === DivisionEnum.Location &&
          divisionCheck.location && (
            <>
              <HStack mt={3} gridColumn='1/2'>
                <Text w='80%' align='right'>
                  英語メッセージ：
                </Text>
              </HStack>
              <HStack mt={3} gridColumn='2/2'>
                <CustomStringInput
                  CheckInfo={GetMessageInputCheckData('')}
                  inputValue={englishMessage}
                  setValue={setEnglishMessage}
                  PhohibitionCharaCheck={false}
                />
              </HStack>
              <HStack mt={3} gridColumn='1/2'>
                <Text w='80%' align='right'>
                  リンク：
                </Text>
              </HStack>
              <HStack mt={3} gridColumn='2/2'>
                <CustomStringInput
                  CheckInfo={GetMessageInputCheckData('')}
                  inputValue={link}
                  setValue={setLink}
                  PhohibitionCharaCheck={false}
                />
              </HStack>
            </>
          )}
      </Box>
      <VStack mt={3} gridColumn='2/2'>
        <Button
          w='200px'
          h='60px'
          bg='yellow.300'
          onClick={() => {
            SendClick();
          }}
        >
          送信
        </Button>
      </VStack>
    </>
  );
};
